import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth.store';
import { loginRequest, logoutRequest, meRequest, type LoginInput } from './auth.api';

export function useCurrentUser() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const user = await meRequest();
      setUser(user);
      return user;
    },
    enabled: !!accessToken,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => loginRequest(input),
    onSuccess: (data) => {
      setAccessToken(data.accessToken);
      setUser(data.user);
      qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Credenciales invalidas';
      toast.error(message);
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => logoutRequest(),
    onSettled: () => {
      clear();
      qc.clear();
      navigate('/login', { replace: true });
    },
  });
}
